import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const UGCWerbevideo = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      navigate("/?lang=de");
    }, 100);
    return () => clearTimeout(timer);
  }, [navigate]);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Was ist ein UGC Werbevideo?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Ein UGC Werbevideo ist ein Werbespot im User-Generated-Content-Stil. Es sieht aus wie authentischer Nutzer-Content, ist aber professionell für Werbezwecke produziert. UGC Werbevideos erzielen höhere Engagement-Raten als traditionelle Werbung."
        }
      },
      {
        "@type": "Question",
        name: "Was kostet ein UGC Produktvideo?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "UGC Produktvideos bei Fuel72 starten ab 49€. Traditionelle Produktvideoproduktion kostet oft 500-2000€ pro Video."
        }
      },
      {
        "@type": "Question",
        name: "Welche UGC Video-Arten gibt es?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Beliebte UGC Video-Arten sind: Testimonial Videos, Produktvideos, Unboxing Videos, Review Videos, How-To Videos, und Performance Ads für TikTok, Instagram und Meta."
        }
      },
      {
        "@type": "Question",
        name: "Funktioniert UGC für Performance Ads?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Ja! UGC Performance Ads erzielen bis zu 4x höhere Click-Through-Rates und 50% niedrigere CPAs als traditionelle Ads. Sie wirken authentischer und generieren mehr Vertrauen."
        }
      }
    ]
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "UGC Werbevideo Produktion",
    description: "Professionelle UGC Werbevideos, Produktvideos und Testimonial Videos für Social Media Ads",
    provider: {
      "@type": "Organization",
      name: "Fuel72"
    },
    areaServed: "Deutschland",
    serviceType: ["UGC Werbevideo", "UGC Produktvideo", "UGC Testimonial Video", "UGC Performance Ads"]
  };

  return (
    <>
      <Helmet>
        <html lang="de" />
        <title>UGC Werbevideo | UGC Produktvideo & Testimonial Videos | Fuel72</title>
        <meta
          name="description"
          content="UGC Werbevideo ✓ UGC Produktvideo ✓ UGC Testimonial Video ✓ UGC Performance Ads ✓ Für TikTok, Instagram & Meta Ads ✓ Ab 49€ ✓ Lieferung in 72h"
        />
        <meta
          name="keywords"
          content="ugc werbevideo, ugc produktvideo, ugc testimonial video, kundenstimmen video, ugc unboxing video, ugc product review video, ugc performance ads, ugc creative testing, ugc creatives für ads, short form ads erstellen lassen"
        />
        <link rel="canonical" href="https://www.fuel72.com/ugc-werbevideo" />
        
        <meta property="og:type" content="website" />
        <meta property="og:title" content="UGC Werbevideo | UGC Produktvideo & Testimonials" />
        <meta property="og:description" content="Professionelle UGC Werbevideos für Ihre Kampagnen. Produktvideos, Testimonials, Performance Ads. Ab 49€, Lieferung in 72h." />
        <meta property="og:url" content="https://www.fuel72.com/ugc-werbevideo" />
        <meta property="og:image" content="https://www.fuel72.com/fuel72-logo.png" />
        <meta property="og:locale" content="de_DE" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="UGC Werbevideo | Fuel72" />
        <meta name="twitter:description" content="UGC Produktvideos & Testimonials. Für TikTok, Reels & Meta Ads. Ab 49€." />
        
        <link rel="alternate" hrefLang="de" href="https://www.fuel72.com/ugc-werbevideo" />
        <link rel="alternate" hrefLang="en" href="https://www.fuel72.com/ugc-ads" />
        <link rel="alternate" hrefLang="x-default" href="https://www.fuel72.com/ugc-ads" />
        
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>

      <main className="min-h-screen bg-background text-foreground p-8">
        <article className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold mb-6">
            UGC Werbevideo – Produktvideos & Testimonials für Ads
          </h1>
          
          <p className="text-xl text-muted-foreground mb-8">
            Mit professionellen UGC Werbevideos von Fuel72 steigern Sie Ihre Conversion-Rates 
            auf TikTok, Instagram und Meta Ads. Authentische Produktvideos, Testimonials 
            und Performance Ads – ab 49€.
          </p>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold mb-4">UGC Video-Arten für Ihre Kampagnen</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="border border-border rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-2">🎬 UGC Produktvideo</h3>
                <p className="text-muted-foreground mb-3">
                  Zeigen Sie Ihr Produkt im Einsatz. Authentische Darstellung, 
                  die Vertrauen schafft und zum Kauf motiviert.
                </p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Produkt-Demos</li>
                  <li>• Feature-Highlights</li>
                  <li>• Anwendungsvideos</li>
                </ul>
              </div>
              <div className="border border-border rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-2">⭐ UGC Testimonial Video</h3>
                <p className="text-muted-foreground mb-3">
                  Kundenstimmen, die überzeugen. Echte Erfahrungsberichte 
                  für maximale Glaubwürdigkeit.
                </p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Kundenbewertungen</li>
                  <li>• Erfolgsgeschichten</li>
                  <li>• Before/After Stories</li>
                </ul>
              </div>
              <div className="border border-border rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-2">📦 UGC Unboxing Video</h3>
                <p className="text-muted-foreground mb-3">
                  Das Auspackerlebnis einfangen. Perfekt für E-Commerce 
                  und D2C Brands.
                </p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• First Impressions</li>
                  <li>• Package Reveals</li>
                  <li>• Produkt-Reaktionen</li>
                </ul>
              </div>
              <div className="border border-border rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-2">🎯 UGC Performance Ads</h3>
                <p className="text-muted-foreground mb-3">
                  Conversion-optimierte Creatives für bezahlte Werbung 
                  auf allen Plattformen.
                </p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• TikTok Ads</li>
                  <li>• Meta/Facebook Ads</li>
                  <li>• Instagram Reels Ads</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold mb-4">UGC für jede Plattform</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-muted/30 border border-border rounded-lg p-4 text-center">
                <div className="text-3xl mb-2">📱</div>
                <h3 className="font-semibold">UGC für TikTok</h3>
                <p className="text-sm text-muted-foreground">9:16 Format, Trends, Hooks</p>
              </div>
              <div className="bg-muted/30 border border-border rounded-lg p-4 text-center">
                <div className="text-3xl mb-2">📸</div>
                <h3 className="font-semibold">UGC für Instagram Reels</h3>
                <p className="text-sm text-muted-foreground">Reels-optimiert, ästhetisch</p>
              </div>
              <div className="bg-muted/30 border border-border rounded-lg p-4 text-center">
                <div className="text-3xl mb-2">📊</div>
                <h3 className="font-semibold">UGC für Meta Ads</h3>
                <p className="text-sm text-muted-foreground">Conversion-fokussiert</p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold mb-4">Warum UGC Werbevideos besser performen</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">📈</span>
                  <div>
                    <h3 className="font-semibold">4x höhere Engagement-Rate</h3>
                    <p className="text-muted-foreground text-sm">UGC erzielt mehr Likes, Kommentare und Shares als polierte Werbung.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-2xl">💰</span>
                  <div>
                    <h3 className="font-semibold">50% niedrigere CPAs</h3>
                    <p className="text-muted-foreground text-sm">Authentischer Content konvertiert besser bei niedrigeren Kosten.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-2xl">🎯</span>
                  <div>
                    <h3 className="font-semibold">Höhere View-Through-Rate</h3>
                    <p className="text-muted-foreground text-sm">Nutzer schauen UGC länger als traditionelle Werbung.</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">🤝</span>
                  <div>
                    <h3 className="font-semibold">Mehr Vertrauen</h3>
                    <p className="text-muted-foreground text-sm">92% der Konsumenten vertrauen UGC mehr als Markenwerbung.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-2xl">🔄</span>
                  <div>
                    <h3 className="font-semibold">Perfekt für A/B Testing</h3>
                    <p className="text-muted-foreground text-sm">Testen Sie verschiedene Hooks, CTAs und Stile.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-2xl">⚡</span>
                  <div>
                    <h3 className="font-semibold">Schnelle Iteration</h3>
                    <p className="text-muted-foreground text-sm">Neue Creatives in 72h statt Wochen.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold mb-4">UGC Werbevideo Pakete</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="border border-border rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-2">Starter</h3>
                <p className="text-3xl font-bold mb-4">147€</p>
                <ul className="space-y-2 text-muted-foreground mb-4">
                  <li>✓ 3 UGC Werbevideos</li>
                  <li>✓ 1 Video-Stil</li>
                  <li>✓ 72h Lieferung</li>
                  <li>✓ Revisionen inklusive</li>
                </ul>
                <p className="text-sm text-muted-foreground">Ideal für: Erste Tests</p>
              </div>
              <div className="border border-primary rounded-lg p-6 relative">
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-3 py-1 text-sm rounded-full">Beliebt</span>
                <h3 className="text-xl font-semibold mb-2">Pro</h3>
                <p className="text-3xl font-bold mb-4">225€</p>
                <ul className="space-y-2 text-muted-foreground mb-4">
                  <li>✓ 5 UGC Werbevideos</li>
                  <li>✓ 2 Video-Stile</li>
                  <li>✓ 72h Lieferung</li>
                  <li>✓ Revisionen inklusive</li>
                </ul>
                <p className="text-sm text-muted-foreground">Ideal für: A/B Testing</p>
              </div>
              <div className="border border-border rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-2">Scale</h3>
                <p className="text-3xl font-bold mb-4">390€</p>
                <ul className="space-y-2 text-muted-foreground mb-4">
                  <li>✓ 10 UGC Werbevideos</li>
                  <li>✓ Alle Video-Stile</li>
                  <li>✓ 72h Lieferung</li>
                  <li>✓ Revisionen inklusive</li>
                </ul>
                <p className="text-sm text-muted-foreground">Ideal für: Skalierung</p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold mb-4">Häufige Fragen zu UGC Werbevideos</h2>
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold mb-2">Welches UGC Video-Format funktioniert am besten?</h3>
                <p className="text-muted-foreground">Das hängt von Ihrer Zielgruppe und Plattform ab. Für TikTok und Reels empfehlen wir kurze, hook-starke Videos. Für Meta Ads funktionieren längere Testimonials oft besser.</p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Kann ich die Videos für alle Plattformen nutzen?</h3>
                <p className="text-muted-foreground">Ja! Alle Nutzungsrechte sind inklusive. Sie können die Videos auf TikTok, Instagram, Facebook, YouTube und allen anderen Plattformen einsetzen.</p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Wie viele Videos brauche ich für effektives Creative Testing?</h3>
                <p className="text-muted-foreground">Wir empfehlen mindestens 5 Videos für aussagekräftige A/B Tests. Mit dem Pro-Paket können Sie verschiedene Hooks, CTAs und Stile testen.</p>
              </div>
            </div>
          </section>

          <p className="text-center text-muted-foreground">
            Weiterleitung zu Fuel72.com...
          </p>
        </article>
      </main>
    </>
  );
};

export default UGCWerbevideo;
